import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { CheckCircle, Loader2, MessageSquare } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactSection = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    try {
      const response = await fetch(`${BACKEND_URL}/api/waiting-list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          user_type: 'resident',
          pain_points: `Contact enquiry:\n${formData.message}`,
          interest_type: 'contact',
          source_page: (typeof window !== 'undefined' ? window.location.pathname : '/'),
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(typeof errorData.detail === 'string' ? errorData.detail : 'Failed to send');
      }
      setIsSuccess(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 lg:py-20 bg-white scroll-mt-20" data-testid="contact-section">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-[#B5541B]/10 px-4 py-2 rounded-full mb-5">
            <MessageSquare className="w-4 h-4 text-[#B5541B]" />
            <span className="text-sm font-semibold text-[#B5541B]">Contact</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1F1E1E] mb-3">Questions? Send us a message</h2>
          <p className="text-base text-[#3B3535]/70">
            A general enquiry — nothing to do with registering. We&apos;ll reply by email.
          </p>
        </div>

        {isSuccess ? (
          <div data-testid="contact-success" className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 text-center">
            <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-7 h-7 text-green-600" />
            </div>
            <p className="text-[#1F1E1E] font-semibold">Message received — we&apos;ll get back to you by email.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-[#FFFAF8] rounded-2xl p-6 sm:p-8 border border-gray-100 space-y-4" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact-name" className="text-[#3B3535] font-medium">Name <span className="text-[#B5541B]">*</span></Label>
                <Input
                  id="contact-name" data-testid="contact-name-input" type="text" required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your name"
                  className="border-gray-200 focus:border-[#B5541B] focus:ring-[#B5541B] bg-white"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-email" className="text-[#3B3535] font-medium">Email <span className="text-[#B5541B]">*</span></Label>
                <Input
                  id="contact-email" data-testid="contact-email-input" type="email" required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="you@example.com"
                  className="border-gray-200 focus:border-[#B5541B] focus:ring-[#B5541B] bg-white"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-message" className="text-[#3B3535] font-medium">Message <span className="text-[#B5541B]">*</span></Label>
              <Textarea
                id="contact-message" data-testid="contact-message-input" required rows={4}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="How can we help?"
                className="border-gray-200 focus:border-[#B5541B] focus:ring-[#B5541B] resize-none bg-white"
              />
            </div>
            {error && <div data-testid="contact-error" className="bg-red-50 text-red-600 p-3 rounded-lg text-sm" role="alert">{error}</div>}
            <Button
              type="submit" data-testid="contact-submit-btn" disabled={isSubmitting}
              className="w-full bg-[#1F1E1E] hover:bg-black text-white font-semibold py-5 rounded-full shadow-md transition-all duration-300 disabled:opacity-50"
            >
              {isSubmitting ? (<><Loader2 className="mr-2 w-4 h-4 animate-spin" />Sending...</>) : 'Send message'}
            </Button>
          </form>
        )}

        <p className="mt-8 text-center text-sm text-[#3B3535]/70">
          <a href="https://www.teqie.online" target="_blank" rel="noopener noreferrer" className="font-semibold text-[#1F1E1E] hover:text-[#B5541B] transition-colors">Teqie Ltd</a>
          {' '}&bull;{' '}
          <a href="https://www.linkedin.com/in/shakirahakinwale" target="_blank" rel="noopener noreferrer" className="text-[#B5541B] hover:text-[#8B3F14] transition-colors">Shakirah Akinwale</a>
          {' '}&bull;{' '}
          <a href="mailto:Shax@teqie.online" className="text-[#3B3535] hover:text-[#B5541B] transition-colors">Shax@teqie.online</a>
        </p>
      </div>
    </section>
  );
};

export default ContactSection;
